import { CLAIMABLE_STATUSES, type AccountReward } from "@/lib/api/rewards";

/* What changed between two sightings of the player's rewards, and which of
   them the player has already looked at.

   The platform pushes no reward events — the whitelabel watches its own
   polling the same way: keep the last status per reward, diff on every fresh
   list, and announce only genuine transitions. The first sighting seeds the
   baseline silently; a page load is not news.

   Seen-state is a per-browser convenience, so it lives in localStorage behind
   try/catch, and losing it costs a dot on the gift button, nothing more. */

export type RewardSnapshot = {
  status: AccountReward["status"];
  trancheStatus: string | null;
};

export type RewardTransition = {
  reward: AccountReward;
  kind: "claimable" | "expired" | "wagering-completed" | "wagering-exhausted";
};

export const snapshotRewards = (rewards: AccountReward[]): Map<string, RewardSnapshot> => {
  const map = new Map<string, RewardSnapshot>();

  for (const reward of rewards) {
    map.set(reward.id, {
      status: reward.status,
      trancheStatus: reward.tranche?.status ?? null,
    });
  }

  return map;
};

export const rewardTransitions = (
  previous: Map<string, RewardSnapshot>,
  rewards: AccountReward[],
): RewardTransition[] => {
  const transitions: RewardTransition[] = [];

  for (const reward of rewards) {
    const before = previous.get(reward.id);

    /* New to the list entirely — the indicator handles it; a toast for every
       reward that existed before the tab opened would be noise. */
    if (!before) continue;

    if (
      !CLAIMABLE_STATUSES.includes(before.status) &&
      CLAIMABLE_STATUSES.includes(reward.status)
    ) {
      transitions.push({ reward, kind: "claimable" });
      continue;
    }

    if (before.status !== "expired" && reward.status === "expired") {
      transitions.push({ reward, kind: "expired" });
      continue;
    }

    const trancheNow = reward.tranche?.status ?? null;

    if (before.trancheStatus !== trancheNow && trancheNow === "completed") {
      transitions.push({ reward, kind: "wagering-completed" });
      continue;
    }

    if (before.trancheStatus !== trancheNow && trancheNow === "exhausted") {
      transitions.push({ reward, kind: "wagering-exhausted" });
    }
  }

  return transitions;
};

/* ------------------------------------------------------------ seen state */

const SEEN_KEY = "rewards-seen";
const SEEN_CAP = 200;

export const seenRewardIds = (): Set<string> => {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
};

export const markRewardsSeen = (ids: string[]) => {
  try {
    const seen = seenRewardIds();
    for (const id of ids) seen.add(id);
    /* Old ids never leave the platform list order-first, so cap from the
       front — a seen-list that grows forever is a tiny leak with no upside. */
    localStorage.setItem(SEEN_KEY, JSON.stringify([...seen].slice(-SEEN_CAP)));
  } catch {
    /* Private mode — the dot shows again next visit, which is harmless. */
  }
};

/* The gift button's dot: something is claimable, or something is new since
   the player last opened the panel. */
export const hasRewardActivity = (rewards: AccountReward[], seen: Set<string>): boolean =>
  rewards.some(
    (reward) => CLAIMABLE_STATUSES.includes(reward.status) || !seen.has(reward.id),
  );
